export const LIVE_WAVEFORM_BAR_COUNT = 36;
export const LIVE_WAVEFORM_UPDATE_INTERVAL_MS = 66;

export type LiveRecordingAnalyser = {
  sampleBarHeights: () => readonly number[];
  dispose: () => void;
};

function normalizeAmplitude(byte: number): number {
  const normalized = byte / 255;
  return Math.min(1, Math.max(0.12, normalized * 1.35));
}

export function createLiveRecordingAnalyser(
  stream: MediaStream,
  barCount: number = LIVE_WAVEFORM_BAR_COUNT,
): LiveRecordingAnalyser {
  const audioContext = new AudioContext();
  const source = audioContext.createMediaStreamSource(stream);
  const analyser = audioContext.createAnalyser();
  analyser.fftSize = 256;
  analyser.smoothingTimeConstant = 0.72;
  source.connect(analyser);

  const frequencyData = new Uint8Array(analyser.frequencyBinCount);
  const barHeights = new Array<number>(barCount).fill(0.18);
  let disposed = false;

  return {
    sampleBarHeights(): readonly number[] {
      if (disposed) {
        return barHeights;
      }
      analyser.getByteFrequencyData(frequencyData);
      const step = Math.max(1, Math.floor(frequencyData.length / barCount));
      for (let index = 0; index < barCount; index += 1) {
        const sample = frequencyData[index * step] ?? 0;
        const next = normalizeAmplitude(sample);
        barHeights[index] = barHeights[index]! * 0.55 + next * 0.45;
      }
      return barHeights;
    },
    dispose(): void {
      if (disposed) {
        return;
      }
      disposed = true;
      source.disconnect();
      analyser.disconnect();
      void audioContext.close();
    },
  };
}

export function recordingWaveformProgress(elapsedMs: number, windowMs: number = 60_000): number {
  if (!Number.isFinite(elapsedMs) || elapsedMs <= 0) {
    return 0.08;
  }
  return Math.min(1, Math.max(0.08, elapsedMs / windowMs));
}
